import React, { useState } from 'react';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../context/AuthContext';
import './LubeSales.css';

const EMPTY = { sale_date: today(), customer_id: '', customer_name: 'Cash', product_name: '', qty: '', rate: '', tax_per: 18 };

export default function LubeSales() {
  const { user, can, canAccess } = useAuth();
  const isAdmin = can('lube_sales', 'approve');
  const { data: customers } = useApi(() => api.customers.list(), []);
  const { data: allProds } = useApi(() => api.products.list(), []);
  const { data: sales, loading, error, reload } = useApi(() => api.lubeSales.list(), []);

  const lubeProds = (allProds || []).filter(p => p.type === 'Lubricant');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState(null);
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  const isToday = (d) => d === today();
  if (!canAccess('lube_sales')) {
    return (
      <div className="lube-sales-page">
        <ErrBanner msg="Access Denied: You do not have permission to access lube sales." />
      </div>
    );
  }
  const handleSave = async () => {
    if (!form.qty || !form.rate) { notify('❌ Qty and Rate required'); return; }
    setSaving(true);
    try {
      await api.lubeSales.create({
        sale_date: form.sale_date, customer_id: form.customer_id || null,
        customer_name: form.customer_name, product_name: form.product_name,
        qty: parseFloat(form.qty), rate: parseFloat(form.rate), tax_per: parseFloat(form.tax_per),
      });
      notify('✅ Lube sale saved'); setShowForm(false); setForm(EMPTY); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await api.lubeSales.update(editRow.id, { product_name: editRow.product_name, qty: parseFloat(editRow.qty), rate: parseFloat(editRow.rate), tax_per: parseFloat(editRow.tax_per) });
      notify('✅ Updated'); setEditRow(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try { await api.lubeSales.remove(confirm.row.id); notify('✅ Deleted'); reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const handleExport = () => {
    const rows = sales || [];
    if (!rows.length) { notify('❌ No records to export'); return; }
    const printWindow = window.open('', '_blank');
    if (!printWindow) { notify('❌ Pop-up blocked. Please allow pop-ups to export.'); return; }
    const todayStr = new Date().toLocaleDateString();
    const totalAmount = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
    const totalTax = rows.reduce((s, r) => s + Number(r.tax || 0), 0);
    const totalQty = rows.reduce((s, r) => s + Number(r.qty || 0), 0);

    const tableHtml = `
      <html>
        <head>
          <title>Lube Sales Report - ${todayStr}</title>
          <style>
            body { font-family: 'Nunito', sans-serif; padding: 20px; color: #333; }
            h2 { color: #1a6fad; margin-bottom: 5px; }
            .meta { font-size: 12px; color: #666; margin-bottom: 20px; border-bottom: 2px solid #eee; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { background: #f8fafc; color: #64748b; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; padding: 10px; border: 1px solid #e2e8f0; text-align: left; }
            td { padding: 10px; border: 1px solid #e2e8f0; font-size: 12px; }
            .text-right { text-align: right; }
            .total-row { background: #f1f5f9; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Lube Sales Report</h2>
          <div class="meta">Generated on: ${todayStr} | Total Records: ${rows.length} | Total Amount: ₹${fmtNum(totalAmount)}</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Date</th>
                <th>Bill No</th>
                <th>Customer</th>
                <th>Product</th>
                <th class="text-right">Qty</th>
                <th class="text-right">Rate (₹)</th>
                <th class="text-right">GST %</th>
                <th class="text-right">Tax (₹)</th>
                <th class="text-right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map((s, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${fmtDate(s.sale_date)}</td>
                  <td style="font-weight: bold; color: #1a6fad;">${s.bill_no || ''}</td>
                  <td>${s.customer_name || ''}</td>
                  <td>${s.product_name || ''}</td>
                  <td class="text-right">${s.qty}</td>
                  <td class="text-right">₹${fmtNum(s.rate)}</td>
                  <td class="text-right">${s.tax_per}%</td>
                  <td class="text-right">₹${fmtNum(s.tax)}</td>
                  <td class="text-right">₹${fmtNum(s.amount)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="5" class="text-right">TOTAL</td>
                <td class="text-right">${fmtNum(totalQty)}</td>
                <td colspan="2"></td>
                <td class="text-right">₹${fmtNum(totalTax)}</td>
                <td class="text-right">₹${fmtNum(totalAmount)}</td>
              </tr>
            </tbody>
          </table>
          <script>window.print();</script>
        </body>
      </html>
    `;

    printWindow.document.write(tableHtml);
    printWindow.document.close();
  };

  return (
    <div className="lube-sales-page">
      <div className="page-header">
        <h2 className="page-title">Lube Sales</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {can('lube_sales', 'create') && <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>＋ New Sale</button>}
          <button className="export-btn" onClick={handleExport}>⬆ Export</button>
        </div>
      </div>
      {msg && <div style={{ padding: '6px 12px', background: '#f0fff4', border: '1px solid #9ae6b4', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>{msg}</div>}

      {showForm && (
        <div className="section-card">
          <div className="section-header">New Lube Sale</div>
          <div className="form-row">
            <div className="form-group"><label>Date</label>
              <input type="date" className="form-control" value={form.sale_date} onChange={e => setForm({ ...form, sale_date: e.target.value })} />
            </div>
            <div className="form-group"><label>Customer</label>
              <select className="form-control" value={form.customer_id} onChange={e => {
                const c = (customers || []).find(x => String(x.id) === e.target.value);
                setForm({ ...form, customer_id: e.target.value, customer_name: c ? c.name : 'Cash' });
              }}>
                <option value="">Cash</option>
                {(customers || []).filter(c => c.type === 'Credit').map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Product</label>
              <select className="form-control" value={form.product_name} onChange={e => {
                const p = lubeProds.find(x => x.name === e.target.value);
                setForm({ ...form, product_name: e.target.value, rate: p ? p.rate : form.rate, tax_per: p ? p.tax_per : form.tax_per });
              }}>
                <option value="">— Select Product —</option>
                {lubeProds.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Qty (Ltr)</label>
              <input type="number" className="form-control" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} />
            </div>
            <div className="form-group"><label>Rate (₹)</label>
              <input type="number" className="form-control" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} />
            </div>
            <div className="form-group"><label>GST %</label>
              <input type="number" className="form-control" value={form.tax_per} onChange={e => setForm({ ...form, tax_per: e.target.value })} />
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : '💾 Save'}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {editRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ Edit Lube Sale — {editRow.bill_no} — {fmtDate(editRow.sale_date)}</div>
          <div className="form-row">
            <div className="form-group"><label>Product</label>
              <select className="form-control" value={editRow.product_name} onChange={e => {
                const p = lubeProds.find(x => x.name === e.target.value);
                setEditRow({ ...editRow, product_name: e.target.value, rate: p ? p.rate : editRow.rate, tax_per: p ? p.tax_per : editRow.tax_per });
              }}>
                {lubeProds.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Qty (Ltr)</label>
              <input type="number" className="form-control" value={editRow.qty} onChange={e => setEditRow({ ...editRow, qty: e.target.value })} />
            </div>
            <div className="form-group"><label>Rate (₹)</label>
              <input type="number" className="form-control" value={editRow.rate} onChange={e => setEditRow({ ...editRow, rate: e.target.value })} />
            </div>
            <div className="form-group"><label>GST %</label>
              <input type="number" className="form-control" value={editRow.tax_per} onChange={e => setEditRow({ ...editRow, tax_per: e.target.value })} />
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? 'Saving…' : '💾 Update'}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ Cancel</button>
          </div>
        </div>
      )}

      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}

      {!loading && (
        <div className="section-card">
          <div className="section-header">Lube Sale Records</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th style={{ textAlign: 'left' }}>#</th><th style={{ textAlign: 'left' }}>Date</th>
                <th style={{ textAlign: 'left' }}>Bill No</th><th style={{ textAlign: 'left' }}>Customer</th>
                <th style={{ textAlign: 'left' }}>Product</th><th>Qty</th>
                <th>Rate (₹)</th><th>GST %</th><th>Tax (₹)</th><th>Amount (₹)</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr></thead>
              <tbody>
                {(sales || []).map((s, i) => (
                  <tr key={s.id}>
                    <td>{i + 1}</td><td>{fmtDate(s.sale_date)}</td>
                    <td style={{ fontWeight: 700, color: 'var(--primary)', fontFamily: 'monospace' }}>{s.bill_no}</td>
                    <td>{s.customer_name}</td><td>{s.product_name}</td>
                    <td className="right-align">{s.qty}</td>
                    <td className="right-align">₹{fmtNum(s.rate)}</td>
                    <td className="right-align">{s.tax_per}%</td>
                    <td className="right-align">₹{fmtNum(s.tax)}</td>
                    <td className="right-align">₹{fmtNum(s.amount)}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {can('lube_sales', 'update') && (isAdmin || isToday(s.sale_date)) && (
                        <button title="Edit" style={btnEdit} onClick={() => setEditRow({ ...s })}>✏️</button>
                      )}
                      {can('lube_sales', 'delete') && (isAdmin || isToday(s.sale_date)) && (
                        <button title="Delete" style={btnDel} onClick={() => setConfirm({ row: s })}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#e8f0f7', fontWeight: 700 }}>
                  <td colSpan={9} style={{ padding: '8px 10px', textAlign: 'right' }}>Total Amount:</td>
                  <td className="right-align" style={{ color: 'var(--primary-dark)' }}>
                    ₹{fmtNum((sales || []).reduce((s, r) => s + Number(r.amount || 0), 0))}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title="Delete Lube Sale"
        message={`Delete lube sale ${confirm?.row?.bill_no}? The record will be soft-deleted.`}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
